import React, { useState, useEffect, useCallback, useRef } from 'react';
import { devFilesApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import {
  RefreshCw, Plus, X, Eye, Pencil, Trash2, Save,
  ChevronLeft, ChevronRight, Clock, AlertTriangle, Archive, Search, ClipboardCheck, GripVertical
} from 'lucide-react';

/**
 * TaskLogPage - Development page to view/edit task-log.json
 * Uses unified list/detail pattern matching data modules
 * TODO: Delete this file before production deployment
 */
// Available owners for assignment
const OWNERS = ['josh', 'danny', 'todd'];

const TaskLogPage = () => {
  const [taskLog, setTaskLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending'); // pending, in_progress, validate, complete, blocker
  const [listCollapsed, setListCollapsed] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTask, setNewTask] = useState({ task: '', details: '', source: 'Task Log' });
  const [editingTask, setEditingTask] = useState(null);
  const [currentUser, setCurrentUser] = useState(() => {
    return localStorage.getItem('pmd-current-user') || null;
  });
  const [ownerFilter, setOwnerFilter] = useState('all'); // 'all' or specific owner name
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const menuRef = useRef(null);
  const [showArchive, setShowArchive] = useState(false);
  const [archivedTasks, setArchivedTasks] = useState([]);
  const [archiveLoading, setArchiveLoading] = useState(false);

  const { notify } = useNotifications();

  const loadTaskLog = useCallback(async () => {
    try {
      setLoading(true);
      const response = await devFilesApi.getTaskLogJson();
      setTaskLog(response.data);
      // Auto-select first task in current filter if none selected
      if (!selectedTask && response.data.tasks?.length > 0) {
        const filtered = filterTasks(response.data.tasks, statusFilter);
        if (filtered.length > 0) {
          setSelectedTask(filtered[0]);
        }
      }
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to load task log');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load archived tasks
  const loadArchive = useCallback(async () => {
    try {
      setArchiveLoading(true);
      const response = await devFilesApi.getTasksArchive();
      setArchivedTasks(response.data.tasks || []);
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to load archive');
    } finally {
      setArchiveLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Archive completed tasks (moves them from task-log.json to tasks-archive.json)
  const handleArchiveCompleted = async () => {
    const completedCount = getStatusCount('complete');
    if (completedCount === 0) {
      notify.info('No completed tasks to archive');
      return;
    }
    notify.confirm(`Archive ${completedCount} completed tasks?`, async () => {
      try {
        setSaving(true);
        await devFilesApi.archiveCompletedTasks();
        notify.success(`Archived ${completedCount} tasks`);
        await loadTaskLog();
        if (showArchive) {
          await loadArchive();
        }
        // If we were viewing complete tab, switch to pending
        if (statusFilter === 'complete') {
          setStatusFilter('pending');
        }
      } catch (err) {
        notify.error(err.response?.data?.message || 'Failed to archive tasks');
      } finally {
        setSaving(false);
      }
    });
  };

  useEffect(() => {
    loadTaskLog();
  }, [loadTaskLog]);

  // Load archive when toggled on
  useEffect(() => {
    if (showArchive) {
      loadArchive();
    }
  }, [showArchive, loadArchive]);

  // Close user dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get task counts per owner (non-complete only)
  const getOwnerStats = useCallback(() => {
    if (!taskLog?.tasks) return {};
    const stats = {};
    taskLog.tasks.forEach(task => {
      const status = task.status?.toLowerCase();
      if (status === 'complete' || status === 'completed') return; // Skip completed
      const owner = task.owner || 'unassigned';
      if (!stats[owner]) stats[owner] = { not_started: 0, pending: 0, in_progress: 0, validate: 0, total: 0 };
      stats[owner].total++;
      if (status === 'not_started' || status === 'not started') stats[owner].not_started++;
      else if (status === 'pending') stats[owner].pending++;
      else if (status === 'in progress' || status === 'in_progress') stats[owner].in_progress++;
      else if (status === 'validate') stats[owner].validate++;
    });
    return stats;
  }, [taskLog]);

  // Handle user selection (sets both current user for defaults AND filter)
  const handleSelectUser = (user) => {
    if (user !== 'all' && user !== 'unassigned') {
      setCurrentUser(user);
      localStorage.setItem('pmd-current-user', user);
    }
    setOwnerFilter(user);
  };

  const formatOwnerName = (owner) => {
    if (!owner || owner === 'unassigned') return 'Unassigned';
    return owner.charAt(0).toUpperCase() + owner.slice(1);
  };

  // Track if edit form has changes
  const hasChanges = editingTask && selectedTask && (
    editingTask.task !== selectedTask.task ||
    editingTask.details !== selectedTask.details ||
    editingTask.source !== selectedTask.source ||
    editingTask.owner !== selectedTask.owner ||
    editingTask.status !== selectedTask.status
  );

  // Filter tasks by status and owner
  const filterTasks = (tasks, filter, ownerFilterValue) => {
    if (!tasks) return [];
    if (filter === 'blocker') {
      // Show blockers from the blockers array
      return [];
    }
    return tasks.filter(t => {
      // First filter by status
      const status = t.status?.toLowerCase();
      let statusMatch = true;
      if (filter === 'not_started') statusMatch = status === 'not_started' || status === 'not started';
      else if (filter === 'pending') statusMatch = status === 'pending';
      else if (filter === 'in_progress') statusMatch = status === 'in progress' || status === 'in_progress';
      else if (filter === 'validate') statusMatch = status === 'validate';
      else if (filter === 'complete') statusMatch = status === 'complete' || status === 'completed';

      // Then filter by owner (if not 'all')
      let ownerMatch = true;
      if (ownerFilterValue && ownerFilterValue !== 'all') {
        if (ownerFilterValue === 'unassigned') {
          ownerMatch = !t.owner;
        } else {
          ownerMatch = t.owner === ownerFilterValue;
        }
      }

      return statusMatch && ownerMatch;
    });
  };

  const filteredTasks = filterTasks(taskLog?.tasks, statusFilter, ownerFilter);

  // Track if filter tab was explicitly clicked (vs data reload)
  const filterClickedRef = useRef(false);

  // Update selected task when filter changes
  useEffect(() => {
    if (taskLog?.tasks) {
      const filtered = filterTasks(taskLog.tasks, statusFilter);
      if (filtered.length > 0) {
        // Only auto-select first if filter was explicitly clicked or no selection exists
        if (!selectedTask) {
          setSelectedTask(filtered[0]);
        } else if (!filtered.find(t => t.id === selectedTask.id)) {
          // Task moved out of filter - only auto-select if filter was clicked
          if (filterClickedRef.current) {
            setSelectedTask(filtered[0]);
          } else {
            // Task moved via action (accept/retry) - just deselect
            setSelectedTask(null);
          }
        }
      } else {
        setSelectedTask(null);
      }
    }
    filterClickedRef.current = false;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, taskLog]);

  const getEnteredTimestamp = () => {
    const now = new Date();
    const yy = String(now.getFullYear()).slice(-2);
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const hh = String(now.getHours()).padStart(2, '0');
    const mi = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    return `${yy}${mm}${dd}-${hh}${mi}${ss}`;
  };

  const getNextId = () => {
    if (!taskLog?.tasks?.length) return 1;
    return Math.max(...taskLog.tasks.map(t => t.id)) + 1;
  };

  // Add new task
  const handleAddTask = async () => {
    if (!newTask.task.trim()) return;

    try {
      setSaving(true);
      const res = await devFilesApi.createTask({
        task: newTask.task.trim(),
        status: 'pending',
        details: newTask.details.trim(),
        source: newTask.source || 'Task Log',
        owner: currentUser || null,
        claudeSummary: null
      });

      setNewTask({ task: '', details: '', source: 'Task Log' });
      setShowAddTask(false);
      filterClickedRef.current = true;
      setStatusFilter('pending');
      await loadTaskLog();
      setSelectedTask(res.data);
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to add task');
    } finally {
      setSaving(false);
    }
  };

  // Delete task
  const handleDeleteTask = () => {
    if (!selectedTask) return;
    notify.confirm('Delete this task?', async () => {
      try {
        setSaving(true);
        await devFilesApi.deleteTask(selectedTask.id);
        setSelectedTask(null);
        await loadTaskLog();
      } catch (err) {
        notify.error(err.response?.data?.message || 'Failed to delete task');
      } finally {
        setSaving(false);
      }
    });
  };

  // Save edited task
  const handleSaveEdit = async () => {
    if (!editingTask?.task.trim()) return;

    try {
      setSaving(true);
      await devFilesApi.updateTask(editingTask.id, editingTask);
      setEditingTask(null);
      await loadTaskLog();
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  // Format timestamp for display
  const formatTimestamp = (ts) => {
    if (!ts) return '';
    // Legacy format: YYMMDD-HHMMSS -> MM/DD HH:MM
    const match = ts.match(/^(\d{2})(\d{2})(\d{2})-(\d{2})(\d{2})/);
    if (match) {
      return `${match[2]}/${match[3]} ${match[4]}:${match[5]}`;
    }
    // ISO datetime from SQL
    try {
      const d = new Date(ts);
      if (!isNaN(d)) {
        return `${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
      }
    } catch {}
    return ts;
  };

  // Get status badge class
  const getStatusClass = (status) => {
    const s = status?.toLowerCase();
    if (s === 'not_started' || s === 'not started') return 'status-not-started';
    if (s === 'pending') return 'status-pending';
    if (s === 'in progress' || s === 'in_progress') return 'status-in-progress';
    if (s === 'validate') return 'status-validate';
    if (s === 'complete' || s === 'completed') return 'status-complete';
    return '';
  };

  // Get count for each status
  const getStatusCount = (status) => {
    if (!taskLog?.tasks) return 0;
    if (status === 'blocker') return taskLog.blockers?.length || 0;
    return filterTasks(taskLog.tasks, status).length;
  };

  if (loading) {
    return <div className="dev-page loading">Loading...</div>;
  }

  return (
    <div className="module-container task-log-page">
      {/* Header - Pattern 1 */}
      <div className="module-header">
        <div className="module-header-left">
          <ClipboardCheck size={20} style={{ color: 'var(--color-primary)' }} />
          <h1>Task Log</h1>
          <span className="module-header-meta">
            {taskLog?.tasks?.length || 0} tasks &#8226; {getStatusCount('pending')} pending
          </span>
        </div>
        <div className="module-header-actions">
          {editingTask ? (
            <button className="crud-btn" onClick={handleDeleteTask} disabled={saving} title="Delete" style={{ color: 'var(--color-error)' }}><Trash2 size={18} /></button>
          ) : (
            <button className="crud-btn" onClick={() => setShowAddTask(true)} title="Add Task"><Plus size={18} /></button>
          )}
          <button className={`crud-btn ${!selectedTask && !editingTask ? 'disabled' : ''}`} onClick={editingTask ? () => setEditingTask(null) : () => setEditingTask({ ...selectedTask })} disabled={!selectedTask && !editingTask} title={editingTask ? 'Cancel' : 'Edit'}>
            {editingTask ? <X size={18} /> : <Pencil size={18} />}
          </button>
          <button className={`crud-btn ${!editingTask || !hasChanges ? 'disabled' : ''}`} onClick={handleSaveEdit} disabled={!editingTask || saving || !hasChanges} title="Save"><Save size={18} /></button>
          <div className="action-menu" ref={menuRef}>
            <button className="action-menu-btn" onClick={() => setMenuOpen(!menuOpen)} title="Actions"><GripVertical size={16} /></button>
            {menuOpen && (
              <div className="action-menu-dropdown" style={{ minWidth: '200px' }}>
                <button className="action-menu-item" onClick={() => { setMenuOpen(false); loadTaskLog(); }}><RefreshCw size={16} /><span>Refresh</span></button>
                <button className="action-menu-item" onClick={() => { setMenuOpen(false); setShowArchive(!showArchive); }}><Archive size={16} /><span>{showArchive ? 'Hide Archive' : 'Show Archive'}</span></button>
                {statusFilter === 'complete' && getStatusCount('complete') > 0 && (
                  <button className="action-menu-item" onClick={() => { setMenuOpen(false); handleArchiveCompleted(); }}><Archive size={16} /><span>Archive Completed</span></button>
                )}
                <div style={{ borderTop: '1px solid var(--color-gray-200)', margin: 'var(--spacing-xs) 0' }} />
                <div style={{ padding: 'var(--spacing-xs) var(--spacing-md)', fontSize: '10px', fontWeight: 600, color: 'var(--color-gray-400)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Owner</div>
                {['all', ...OWNERS, 'unassigned'].map(o => (
                  <button key={o} className={`action-menu-item ${ownerFilter === o ? 'selected' : ''}`} onClick={() => { handleSelectUser(o); setMenuOpen(false); }} style={ownerFilter === o ? { backgroundColor: 'var(--color-primary-light)', color: 'var(--color-primary)' } : undefined}>
                    <span>{o === 'all' ? 'All Owners' : formatOwnerName(o)}</span>
                  </button>
                ))}
                <div style={{ borderTop: '1px solid var(--color-gray-200)', margin: 'var(--spacing-xs) 0' }} />
                <div style={{ padding: 'var(--spacing-xs) var(--spacing-md)', fontSize: '10px', fontWeight: 600, color: 'var(--color-gray-400)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</div>
                {['not_started', 'pending', 'in_progress', 'validate', 'complete', 'blocker'].map(s => (
                  <button key={s} className={`action-menu-item ${statusFilter === s ? 'selected' : ''}`} onClick={() => { filterClickedRef.current = true; setStatusFilter(s); setMenuOpen(false); }} style={statusFilter === s ? { backgroundColor: 'var(--color-primary-light)', color: 'var(--color-primary)' } : undefined}>
                    <span>{s === 'not_started' ? 'Not Started' : s === 'in_progress' ? 'In Progress' : s.charAt(0).toUpperCase() + s.slice(1)} ({getStatusCount(s)})</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="list-view">
        {listCollapsed ? (
          <div className="list-panel-collapsed">
            <button onClick={() => setListCollapsed(false)} className="list-panel-expand-btn" title="Expand"><ChevronRight size={16} /></button>
          </div>
        ) : (
          <div className="list-panel task-list-panel">
            <div className="list-panel-header">
              <button onClick={() => setListCollapsed(true)} className="list-panel-collapse-btn" title="Collapse"><ChevronLeft size={16} /></button>
              <div style={{ flex: 1, position: 'relative' }}>
                <Search size={14} style={{ position: 'absolute', left: '8px', top: '10px', color: 'var(--color-gray-400)' }} />
                <input className="form-input" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search tasks..." style={{ paddingLeft: '28px', width: '100%' }} />
              </div>
            </div>

            {showArchive && (
              <div className="archive-section">
                <div className="archive-header"><Archive size={14} /><span>Archived Tasks ({archivedTasks.length})</span></div>
                <div className="archive-list">
                  {archiveLoading ? <div className="archive-loading">Loading archive...</div> : archivedTasks.length > 0 ? archivedTasks.map((task) => (
                    <button key={task.id} onClick={() => setSelectedTask(task)} className={`list-item-btn archived-item ${selectedTask?.id === task.id ? 'selected' : ''}`}>
                      <span className="list-item-primary">{task.task}</span>
                      <span className="list-item-secondary"><Clock size={12} /> {formatTimestamp(task.entered)}</span>
                    </button>
                  )) : <div className="archive-empty">No archived tasks</div>}
                </div>
              </div>
            )}

            <div className="list-scroll">
              {statusFilter === 'blocker' ? (
                taskLog?.blockers?.length > 0 ? taskLog.blockers.map((blocker, idx) => (
                  <div key={idx} className="list-item-btn blocker-item"><AlertTriangle size={14} className="blocker-icon" /><span className="list-item-primary">{blocker}</span></div>
                )) : <div className="task-list-empty">No blockers</div>
              ) : (
                filteredTasks.length > 0 ? filteredTasks.map((task) => (
                  <button key={task.id} onClick={() => setSelectedTask(task)} className={`list-item-btn ${selectedTask?.id === task.id ? 'selected' : ''}`}>
                    <span className="list-item-primary">{task.task}</span>
                    <span className="list-item-secondary"><Clock size={12} /> {formatTimestamp(task.entered)} &#8226; {task.source}</span>
                  </button>
                )) : <div className="task-list-empty">No tasks</div>
              )}
            </div>
          </div>
        )}

        {/* Detail Panel */}
        <div className="detail-panel">
          {selectedTask && statusFilter !== 'blocker' ? (
            <>
              <div className="dev-detail-header">
                <div className="dev-detail-title">
                  <h2>{selectedTask.task}</h2>
                  <span className={`task-status-badge ${getStatusClass(selectedTask.status)}`}>
                    {selectedTask.status}
                  </span>
                </div>
              </div>

              <div className="dev-detail-body">
                <div className="task-detail-meta">
                  <div className="meta-item">
                    <label>Source</label>
                    <span>{selectedTask.source}</span>
                  </div>
                  <div className="meta-item">
                    <label>Owner</label>
                    <span>{selectedTask.owner || 'Unassigned'}</span>
                  </div>
                  <div className="meta-item">
                    <label>Entered</label>
                    <span>{selectedTask.entered}</span>
                  </div>
                  <div className="meta-item">
                    <label>ID</label>
                    <span>{selectedTask.id}</span>
                  </div>
                </div>

                <div className="task-detail-section">
                  <label>Details</label>
                  <div className="task-details-content">
                    {selectedTask.details?.split('\n').map((line, i) => (
                      <p key={i}>{line || '\u00A0'}</p>
                    )) || <p className="no-details">(no details provided)</p>}
                  </div>
                </div>

                {selectedTask.claudeSummary && (
                  <div className="task-detail-section claude-summary-section">
                    <label>Claude's Summary</label>
                    <div className="claude-summary-content">
                      {selectedTask.claudeSummary}
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : statusFilter === 'blocker' ? (
            <div className="dev-detail-empty">
              <AlertTriangle size={48} />
              <p>Blockers are shown in the list panel</p>
              <p className="hint">Items that are blocking progress go here</p>
            </div>
          ) : (
            <div className="dev-detail-empty">
              <Eye size={48} />
              <p>Select a task to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Task Modal */}
      {showAddTask && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add Task</h3>
              <button className="modal-close" onClick={() => setShowAddTask(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <label>Task Description</label>
                <input
                  type="text"
                  className="form-input"
                  value={newTask.task}
                  onChange={(e) => setNewTask({ ...newTask, task: e.target.value })}
                  placeholder="Short description of the task"
                  autoFocus
                />
              </div>
              <div className="form-row">
                <label>Source</label>
                <input
                  type="text"
                  className="form-input"
                  value={newTask.source}
                  onChange={(e) => setNewTask({ ...newTask, source: e.target.value })}
                  placeholder="Where this task came from"
                />
              </div>
              <div className="form-row">
                <label>Details</label>
                <textarea
                  className="form-input form-textarea"
                  value={newTask.details}
                  onChange={(e) => setNewTask({ ...newTask, details: e.target.value })}
                  placeholder="Detailed requirements, context, or notes..."
                  rows={18}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddTask(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddTask}
                disabled={saving || !newTask.task.trim()}
              >
                {saving ? 'Adding...' : 'Add Task'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit/Retry Task Modal */}
      {editingTask && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit Task</h3>
              <button className="modal-close" onClick={() => setEditingTask(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <label>Task Description</label>
                <input
                  type="text"
                  className="form-input"
                  value={editingTask.task}
                  onChange={(e) => setEditingTask({ ...editingTask, task: e.target.value })}
                  autoFocus
                />
              </div>
              <div className="form-row form-row-inline">
                <div className="form-field">
                  <label>Status</label>
                  <select
                    className="form-input"
                    value={editingTask.status || 'not_started'}
                    onChange={(e) => setEditingTask({ ...editingTask, status: e.target.value })}
                  >
                    <option value="not_started">Not Started</option>
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="validate">Validate</option>
                    <option value="complete">Complete</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>Owner</label>
                  <select
                    className="form-input"
                    value={editingTask.owner || ''}
                    onChange={(e) => setEditingTask({ ...editingTask, owner: e.target.value || null })}
                  >
                    <option value="">Unassigned</option>
                    {OWNERS.map(owner => (
                      <option key={owner} value={owner}>
                        {owner.charAt(0).toUpperCase() + owner.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <label>Source</label>
                <input
                  type="text"
                  className="form-input"
                  value={editingTask.source}
                  onChange={(e) => setEditingTask({ ...editingTask, source: e.target.value })}
                />
              </div>
              <div className="form-row">
                <label>Details</label>
                <textarea
                  className="form-input form-textarea"
                  value={editingTask.details}
                  onChange={(e) => setEditingTask({ ...editingTask, details: e.target.value })}
                  rows={18}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditingTask(null)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSaveEdit}
                disabled={saving || !editingTask.task.trim()}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskLogPage;
